<template>
  <v-dialog
    v-model="dialog"
    max-width="1400px"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="headline white--text" style="background-color:#383083;">
        <span>{{ getDialogTitle() }}</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          dark
          @click="closeDialog"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text style="padding: 0;">
        <!-- Show message when no data available -->
        <div v-if="!invoiceData || invoiceData.length === 0" class="text-center pa-8">
          <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-inbox-outline</v-icon>
          <h3 class="text-h6 grey--text text--darken-1 mb-2">Keine Daten verfügbar</h3>
          <p class="text-body-2 grey--text">
            Für die ausgewählte Gruppe wurden keine Einträge gefunden.
          </p>
        </div>
        
        <!-- Show data table when data is available -->
        <v-data-table
          v-else
          :headers="headers"
          :items="invoiceData"
          :search="search"
          dense
          fixed-header
          height="500px"
          class="elevation-1"
          style="overflow-x: auto;"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-text-field
                v-model="search"
                label="Filter"
                clearable
                class="mx-4"
                prepend-inner-icon="mdi-magnify"
              ></v-text-field>
            </v-toolbar>
          </template>

          <!-- Editable fields -->
          <template v-slot:item.pickup_timestamp="{ item }">
            <v-text-field
              v-model="item.pickup_timestamp"
              dense
              hide-details
              @change="updateItem(item)"
            ></v-text-field>
          </template>

          <template v-slot:item.document_no="{ item }">
            <v-text-field
              v-model="item.document_no"
              dense
              hide-details
              @change="updateItem(item)"
            ></v-text-field>
          </template>

          <template v-slot:item.group_main_text="{ item }">
            <v-text-field
              v-model="item.group_main_text"
              dense
              hide-details
              @change="updateItem(item)"
            ></v-text-field>
          </template>

          <template v-slot:item.company="{ item }">
            <v-text-field
              v-model="item.company"
              dense
              hide-details
              @change="updateItem(item)"
            ></v-text-field>
          </template>

          <template v-slot:item.article_name="{ item }">
            <v-text-field
              v-model="item.article_name"
              dense
              hide-details
              @change="updateItem(item)"
              :disabled="item.leerfahrt"
            ></v-text-field>
          </template>

          <template v-slot:item.pickup_qty="{ item }">
            <v-text-field
              v-model="item.pickup_qty"
              type="number"
              dense
              hide-details
              @change="onQuantityChange(item)"
            ></v-text-field>
          </template>

          <template v-slot:item.price_total="{ item }">
            <v-text-field
              v-model="item.price_total"
              type="number"
              step="0.01"
              dense
              hide-details
              @change="updateItem(item)"
            ></v-text-field>
          </template>

          
          <template v-slot:item.leerfahrt="{ item }">
            <v-checkbox
              v-model="item.leerfahrt"
              :key="item.id"
              dense
              hide-details
              @change="onLeerfahrtChange(item)"
            ></v-checkbox>
          </template>

          <template v-slot:item.rest="{ item }">
            <v-text-field
              v-model="item.rest"
              dense
              hide-details
              @change="updateItem(item)"
            ></v-text-field>
          </template>

          <template v-slot:item.invoice="{ item }">
            <v-text-field
              v-model="item.invoice"
              dense
              hide-details
              @change="updateItem(item)"
            ></v-text-field>
          </template>


          <template v-slot:item.actions="{ item }">
            <div class="d-flex">
             <v-tooltip
               v-if="item.pickup_note && item.pickup_note.trim()"
               bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="info"
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="showPickupNote(item)"
                    class="mr-1"
                  >
                    <v-icon>mdi-information</v-icon>
                  </v-btn>
                </template>
                <span>
                  {{ item.pickup_note }}
                </span>

              </v-tooltip>
              <v-btn
                color="error"
                icon
                small
                @click="deleteItem(item)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions>
        <v-row align="center" class="mx-2">
          <v-col cols="auto">
            <span class="text-subtitle-1">
              Gesamt: <strong>{{ calculateTotal() }}€</strong>
            </span>
          </v-col>
          <v-col cols="auto" v-if="invoiceData.some(item => item.leerfahrt)">
            <span class="text-caption text--secondary">
              Leerfahrt: {{ leerfahrtPrice.toFixed(2) }}€
            </span>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            <v-btn
              color="#383083"
              medium
              style="color: white;"
              @click="exportData"
              :loading="isExporting"
            >
              Export
            </v-btn>
          </v-col>



        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "InvoiceDialog",
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    initialData: {
      type: Array,
      default: () => []
    },
    selectedGroupTypes: {
      type: Array,
      default: () => []
    },
    groupList: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      search: '',
      invoiceData: [],
      modifiedItems: new Set(),
      isExporting: false,
      leerfahrtPrice: 25.00,
      originalValues: {}, 
      isInitialLoad: true,
           headers: [
        { text: 'Abholdatum', value: 'pickup_timestamp', sortable: true },
        { text: 'Auftragsnummer', value: 'document_no', sortable: true },
        { text: 'Untergruppe', value: 'group_type_text', sortable: true },
        { text: 'Kunde', value: 'company', sortable: true },
        { text: 'Ort', value: 'city', sortable: true },
        { text: 'Artikel', value: 'article_name', sortable: true },     
        { text: 'Geplant', value: 'order_qty', sortable: true },
        { text: 'Anzahl', value: 'pickup_qty', sortable: true },
        { text: 'Einzelpreis', value: 'price', sortable: true },
        { text: 'Gesamt', value: 'price_total', sortable: true },
        { text: 'Leerfahrt', value: 'leerfahrt', sortable: true },
        { text: 'Reststreifen', value: 'rest', sortable: true },
        { text: 'Aktionen', value: 'actions', sortable: false }
      ]
    }
  },

  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    filteredItems() {
    if (!this.search || this.search.trim() === '') {
      return this.invoiceData
    }
    
    const searchTerm = this.search.toLowerCase()
    return this.invoiceData.filter(item => {
      return Object.values(item).some(value => {
        if (value === null || value === undefined) return false
        return String(value).toLowerCase().includes(searchTerm)
      })
    })
  }
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.loadData()
        this.isInitialLoad = true;
      }
    },
   initialData: {
    handler(newData) {
      if (newData && newData.length > 0 && !this.value) {
        this.invoiceData = [...newData]
      }
    },
    immediate: true
  }
  },

  methods: {
   
    showPickupNote(item) {
    if (item.pickup_note && item.pickup_note.trim()) {
    
      this.$emit('show-snackbar', {
        message: `ℹ️ Notiz: ${item.pickup_note}`,
        color: 'info',
        timeout: 4000
      })
    } else {
     
      this.$emit('show-snackbar', {
        message: '⚠️ Keine Notiz verfügbar',
        color: 'warning',
        timeout: 4000
      })
    }
    },


    formatDateForDisplay(dateString) {
      if (!dateString) return '';
      if (dateString.includes('.') && !dateString.includes(':')) {
        return dateString;
      }
      if (dateString.includes(' ')) {
        dateString = dateString.split(' ')[0];
      }
      if (dateString.includes('T')) {
        dateString = dateString.split('T')[0]; 
      }
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[2].padStart(2, '0')}.${parts[1].padStart(2, '0')}.${parts[0]}`;
      }
      if (dateString.includes(':')) {
        // Split by spaces and dots to find date parts
        const allParts = dateString.replace(/[:.]/g, ' ').split(' ').filter(part => part.length > 0);
        // Look for year (4 digits), month, and day
        const year = allParts.find(part => part.length === 4 && /^\d{4}$/.test(part));
        if (year) {
          const dayMonth = allParts.filter(part => /^\d{1,2}$/.test(part) && part !== year);
          if (dayMonth.length >= 2) {
            const day = dayMonth[0].padStart(2, '0');
            const month = dayMonth[1] ? dayMonth[1].padStart(2, '0') : '01';
            return `${day}.${month}.${year}`;
          }
        }
      }
      
      return dateString;
    },

    
    parseDateString(dateString) {
      if (!dateString) return new Date(0);
      const parts = dateString.split('.');
      if (parts.length === 3) {
        return new Date(parts[2], parts[1] - 1, parts[0]);
      }
      return new Date(dateString);
    },

    //sorting (most recent on top)
    sortDataByDate() {
      this.invoiceData.sort((a, b) => {
        const dateA = this.parseDateString(a.pickup_timestamp);
        const dateB = this.parseDateString(b.pickup_timestamp);
        return dateB - dateA; 
      });
    },
    storeOriginalValues(item) {
      if (!this.originalValues[item.id]) {
        this.originalValues[item.id] = {
          pickup_qty: item.pickup_qty,
          price_total: item.price_total,
          price: item.price,
          article_name: item.article_name
        };
      }
    },

   
    onLeerfahrtChange(item) {
      if (item.leerfahrt) {
        this.storeOriginalValues(item);
        
        item.pickup_qty = 0;
        item.price_total = this.leerfahrtPrice.toFixed(2);
        item.price = this.leerfahrtPrice.toFixed(2);
        item.article_name = "Leerfahrt";
      } else {
        
        if (this.originalValues[item.id]) {
          item.pickup_qty = this.originalValues[item.id].pickup_qty;
          item.price_total = this.originalValues[item.id].price_total;
          item.price = this.originalValues[item.id].price;
          item.article_name = this.originalValues[item.id].article_name;
          
          // Clear the stored original values after restoring
          delete this.originalValues[item.id];
        } else {
          item.pickup_qty = '';
          item.price_total = '';
          item.price = '';
          item.article_name = "";
        }
      }
      this.updateItem(item);
    },

    
    onQuantityChange(item) {
      
      if (!item.pickup_qty || item.pickup_qty === '0' || parseInt(item.pickup_qty) === 0) {
        item.leerfahrt = true;
        item.price_total = this.leerfahrtPrice;
        item.price = this.leerfahrtPrice;
        item.article_name = "Leerfahrt";
      } else {
       
        item.leerfahrt = false;
        const qty = parseFloat(item.pickup_qty) || 0;
        const unitPrice = parseFloat(item.price) || 0;
        item.price_total = (qty * unitPrice).toFixed(2);
      }
      this.updateItem(item);
    },

    
    


async loadArticleNumbers() {
  console.log("Loading article numbers from group_price table");

  const promises = this.invoiceData.map(async (item) => {
    try {
      const param = {
        table: "group_price",
        statement: "select",
        type: "list",
        where: "true",
        selector: "group_type_id", 
        value: item.group_type_id,
        short: "false",
        extension: "",
      };

      const data = {};
      const result = await this.$func.queryAPI(param, data);

      let orgamaxArticle = '10166'; // Default fallback
      
      if (result && result.length > 0) {
        const groupPrice = result[0]; 
        orgamaxArticle = groupPrice.orgamax_article_number || '10166';
        
        console.log(`Found article number for ${item.article_name}: ${orgamaxArticle}`);
      } else {
        console.log(`No article number found for ${item.article_name}, using default`);
      }

      return { 
        ...item, 
        orgamax_article: orgamaxArticle,
        article_number: orgamaxArticle
     
      };

    } catch (error) {
      console.error(`Error loading article number for item ${item.id}:`, error);
      return { 
        ...item, 
        orgamax_article: '10166',
        article_number: '10166'
      };
    }
  });

  const updatedItems = await Promise.all(promises);
  this.invoiceData = updatedItems;
  console.log(" all article numbers loaded");
},


async exportData() {

  if (this.isExporting) {
    console.log('Export already in progress, ignoring...');
    return;
  }
  
  this.isExporting = true;

  console.log('------starting export-----');
  console.log('Invoice data:', this.invoiceData);

  try {

    if (!this.invoiceData || this.invoiceData.length === 0) {
      this.$emit('show-snackbar', {
        message: 'Keine Daten zum Exportieren vorhanden',
        color: 'warning'
      });
      return;
    }

    // Prepare data with all required fields for the XML
    const exportData = this.invoiceData.map(item => {
      const qty = parseFloat(item.pickup_qty) || 0;
      const total = parseFloat(item.price_total) || 0;
      const unitPrice = qty > 0 ? (total / qty) : (parseFloat(item.price) || 0.80);

      let pickupDate = item.pickup_timestamp || item.pickup_date || '';
      if (pickupDate) {

        if (pickupDate.includes('-') && !pickupDate.includes('.')) {
          const dateParts = pickupDate.split(' ')[0].split('-');
          if (dateParts.length === 3) {
            pickupDate = `${dateParts[2]}.${dateParts[1]}.${dateParts[0]}`;
          }
        }
            
      }
     const articleNumber = item.orgamax_article  || '10166';
      return {

        ...item,

        document_no: item.document_no || '',
        company: item.company || '',
        city: item.city || '',
        article_name: item.article_name || '',

        task_id: item.id || item.task_id || '',
        pickup_timestamp: pickupDate,

        unit_price: unitPrice.toFixed(2),
        pickup_qty: parseInt(item.pickup_qty) || 0,

        article_number: articleNumber,
      };
    });

    console.log('Prepared export data for XML:', exportData);

    // Save to API endpoint
    try {
      const saveResponse = await fetch('https://www.migro.net/reifen/orgamax/api/export_data.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify(exportData)
      });

      if (!saveResponse.ok) {
        const errorText = await saveResponse.text();
        throw new Error(`Failed to save data: ${saveResponse.status} - ${errorText}`);
      }

      const saveResult = await saveResponse.json();
      console.log('Data saved successfully:', saveResult);

      this.$emit('show-snackbar', {
        message: `✅ ${saveResult.record_count} Datensätze erfolgreich für orgaMAX exportiert!`,
        color: 'success'
      });
      this.closeDialog();

    } catch (saveError) {
      console.error('Error saving data:', saveError);
      this.$emit('show-snackbar', {
        message: `Fehler beim Speichern: ${saveError.message}`,
        color: 'error'
      });
    }

  } catch (error) {
    console.error('Export error:', error);
    this.$emit('show-snackbar', {
      message: `Fehler beim Export: ${error.message}`,
      color: 'error'
    });
  } finally {
    this.isExporting = false;
  }
},

formatDateForOrgamax(dateString) {
  if (!dateString) {
    return new Date().toISOString().split('T')[0];
  }
  
  // If already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    return dateString.split(' ')[0];
  }
  
  // If in DD.MM.YYYY format
  if (/^\d{2}\.\d{2}\.\d{4}/.test(dateString)) {
    const parts = dateString.split('.');
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  
  // Try to parse as Date object
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  } catch (e) {
    console.error('Error parsing date:', e);
  }
  
  return new Date().toISOString().split('T')[0];
},

    

    formatDate(dateString) {
      if (!dateString) return new Date().toISOString().split('T')[0];
      const parts = dateString.split('.');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
      return new Date().toISOString().split('T')[0];
    },
    getDialogTitle() {
      let title = `Auswahl (${this.invoiceData.length} Stück)`;
      
      if (this.selectedGroupTypes && this.selectedGroupTypes.length > 0) {
        const groupNames = this.selectedGroupTypes.map(id => {
          const group = this.groupList.find(g => g.id === id);
          return group ? group.name : `ID: ${id}`;
        }).join(', ');
        
        title += ` - Filter: ${groupNames}`;
      }
      
      return title;
    },
async loadItemPrices() {
  console.log("Loading item prices from task_items");

  const promises = this.invoiceData.map(async (item) => {
    try {
      const param = {
        table: "task_items",
        statement: "select",
        type: "list",
        where: "true",
        selector: "temp_task_id", 
        value: item.temp_task_id,
        short: "false",
        extension: "",
      };

      const data = {};
      const result = await this.$func.queryAPI(param, data);

      let price = item.price;
      if (result && result.length > 0) {
        const taskItem = result.find(taskItem => 
          taskItem.article_name === item.article_name || 
          taskItem.temp_task_id === item.task_item_id
        );

        if (taskItem) {
          price = parseFloat(taskItem.price) || 0;
        }
      }

      return { ...item, price };

    } catch (error) {
      console.error(`Error loading price for item ${item.id}:`, error);
      return item; 
    }
  });

  const updatedItems = await Promise.all(promises);
  this.invoiceData = updatedItems;

  console.log("✅ All prices loaded");
},

  async loadData() {
  this.originalValues = {};
  
  if (this.initialData && this.initialData.length > 0) {
    this.invoiceData = [...this.initialData];
    this.invoiceData.forEach(item => {
      item.pickup_timestamp = this.formatDateForDisplay(item.pickup_timestamp);
    });
    
     
    
    if (this.isInitialLoad) {
      this.sortDataByDate();
      this.isInitialLoad = false;
    }
    this.loadItemPrices();
    this.loadArticleNumbers();
  } else {
    this.invoiceData = []
  }
},

    updateItem(item) {
      this.modifiedItems.add(item.id)
      this.$emit('item-updated', item)
    },

  
    deleteItem(item) {
      const index = this.invoiceData.findIndex(i => i.id === item.id)
      if (index > -1) {
        this.invoiceData.splice(index, 1)
        this.$emit('item-deleted', item)
      }
    },

  getFilteredCount() {
    return this.filteredItems.length
  },

  calculateTotal() {
    return this.filteredItems.reduce((sum, item) => {
      const price = parseFloat(item.price_total) || 0
      return sum + price
    }, 0).toFixed(2)
  },

    saveChanges() {
      const modifiedData = this.invoiceData.filter(item => 
        this.modifiedItems.has(item.id)
      )
      
      this.$emit('save-changes', {
        all: this.invoiceData,
        modified: modifiedData
      })
      
      this.modifiedItems.clear()
      
      
      this.$emit('show-snackbar', {
        message: 'Änderungen gespeichert!',
        color: 'success'
      })
    },



    closeDialog() {
      this.dialog = false
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  max-height: 500px;
  overflow-x: auto;
}

.v-text-field >>> .v-input__control {
  min-height: 32px;
}

.v-text-field >>> .v-input__slot {
  margin-bottom: 0;
}

.v-checkbox >>> .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}


.d-flex {
  display: flex !important;
  align-items: center;
}
</style>